import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import AddressAndPaymentManager from './AddressAndPaymentManager';
import { userService } from '../../../services/userService';
import { achievementService } from '../../../services/achievementService';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import { Award, TrendingUp, Upload } from 'lucide-react';

export function UserProfile() {
  const { user, refreshUserProfile } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [usernameError, setUsernameError] = useState(''); // NEW state for username specific error
  const [profile, setProfile] = useState(null);
  const [achievements, setAchievements] = useState([]);
  const [achievementStats, setAchievementStats] = useState(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(null);
  
  // Form state for editing
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    username: '',
    bio: '',
    location: '',
    avatarUrl: ''
  });

  useEffect(() => {
    loadUserProfile();
    loadAchievements();
  }, []);

  const loadUserProfile = async () => {
    try {
      setLoading(true);
      setError('');
      setUsernameError(''); // Clear username error on load
      
      // Load all data in parallel - services now handle userId internally
      const profileResult = await userService?.getCurrentUserProfile();

      // Handle profile data
      if (profileResult) {
        setProfile(profileResult);
        setFormData({
          firstName: profileResult?.firstName || '',
          lastName: profileResult?.lastName || '',
          username: profileResult?.username || '',
          bio: profileResult?.bio || '',
          location: profileResult?.location || '',
          avatarUrl: profileResult?.avatarUrl || ''
        });
        setAvatarPreview(profileResult?.avatarUrl || null);
      } else {
        console.error('Profile loading failed: No data returned');
        setError('Failed to load profile');
      }

    } catch (err) {
      console.error('Unexpected error loading user data:', err);
      setError(`Failed to load user data: ${err?.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const loadAllData = async () => {
    await loadUserProfile();
    await loadAchievements();
  };

  const loadAchievements = async () => {
    try {
      // Get current user from Supabase auth
      const { data: { user: currentUser }, error: authError } = await supabase?.auth?.getUser();
      
      if (authError) {
        console.error('Auth error when loading achievements:', authError);
        return;
      }

      if (!currentUser) {
        return;
      }

      const [achievementsData, statsData] = await Promise.all([
        achievementService?.getUserAchievementsWithDetails(currentUser?.id),
        achievementService?.getUserStats(currentUser?.id)
      ]);

      setAchievements(achievementsData || []);
      setAchievementStats(statsData);
    } catch (err) {
      console.error('Error loading achievements:', err);
      // Don't throw - just log the error to avoid breaking the component
    }
  };

  const handleAvatarUpload = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file?.type?.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file?.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB');
      return;
    }

    setUploadingAvatar(true);
    setError('');

    try {
      // Get current user from Supabase auth
      const { data: { user: currentUser }, error: authError } = await supabase?.auth?.getUser();
      
      if (authError) throw authError;
      if (!currentUser) throw new Error('No authenticated user found');

      // Create a unique file name
      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `${currentUser?.id}_${Date.now()}.${fileExt}`;
      const filePath = `avatars/${fileName}`;

      // Upload to Supabase storage
      const uploadResult = await supabase?.storage
        ?.from('user-avatars')
        ?.upload(filePath, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (!uploadResult) {
        throw new Error('Failed to connect to storage service');
      }

      const { data: uploadData, error: uploadError } = uploadResult;

      if (uploadError) throw uploadError;

      // Get public URL (user-avatars is a public bucket)
      const publicUrlResult = supabase?.storage
        ?.from('user-avatars')
        ?.getPublicUrl(filePath);

      if (!publicUrlResult) {
        throw new Error('Failed to generate public URL');
      }

      const { data } = publicUrlResult;

      if (!data?.publicUrl) {
        throw new Error('Failed to generate public URL for uploaded file');
      }

      const avatarUrl = data?.publicUrl;
      
      setFormData(prev => ({ ...prev, avatarUrl }));
      setAvatarPreview(avatarUrl);
    } catch (err) {
      console.error('Error uploading avatar:', err);
      setError(err?.message || 'Failed to upload avatar');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleRemoveAvatar = () => {
    setFormData(prev => ({ ...prev, avatarUrl: '' }));
    setAvatarPreview(null);
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    // Reset form to current profile data
    setFormData({
      firstName: profile?.firstName || '',
      lastName: profile?.lastName || '',
      username: profile?.username || '',
      bio: profile?.bio || '',
      location: profile?.location || '',
      avatarUrl: profile?.avatarUrl || ''
    });
    setAvatarPreview(profile?.avatarUrl || null);
    setError('');
    setUsernameError(''); // Clear username error on cancel
  };

  const handleSave = async () => {
    try {
      setError('');
      setUsernameError(''); // Clear username error before saving
      const updatedProfile = await userService?.updateProfile(formData);
      setProfile(updatedProfile);
      setIsEditing(false);
      refreshUserProfile(); // NEW: Call to refresh header name
    } catch (err) {
      if (err.message === 'Username already taken. Please choose a different one.') {
        setUsernameError(err.message);
      } else {
        setError(err?.message);
      }
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (field === 'username') {
      setUsernameError(''); // Clear username error when username input changes
    }
  };

  if (loading) {
    return (
      <div className="bg-card rounded-lg border border-border p-6 shadow-soft">
        <div className="flex items-center justify-center py-8">
          <Icon name="Loader" className="animate-spin text-primary" size={32} />
          <div className="ml-3">
            <p className="text-muted-foreground font-medium">Loading profile...</p>
            <p className="text-xs text-muted-foreground mt-1">This may take a moment on first login</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card rounded-lg border border-border p-6 shadow-soft">
        <div className="bg-error/10 border border-error rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <Icon name="AlertCircle" className="text-error flex-shrink-0 mt-0.5" size={20} />
            <div className="flex-1">
              <p className="text-error text-sm font-medium mb-1">Error Loading Profile</p>
              <p className="text-error text-sm mb-3">{error}</p>
              <div className="space-y-2">
                <button
                  onClick={loadAllData}
                  className="text-sm text-error underline hover:no-underline font-medium"
                >
                  Try Again
                </button>
                <div className="text-xs text-muted-foreground mt-2 p-3 bg-muted rounded-lg">
                  <p className="font-medium mb-1">Troubleshooting:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Make sure you're logged in</li>
                    <li>Check your internet connection</li>
                    <li>Try refreshing the page</li>
                    <li>If the issue persists, your profile may need to be created manually</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="bg-card rounded-lg border border-border p-6 shadow-soft">
        <div className="text-center py-8">
          <Icon name="AlertCircle" className="text-muted-foreground mx-auto mb-3" size={32} />
          <p className="text-muted-foreground">No profile data available</p>
          <button
            onClick={loadAllData}
            className="mt-3 text-sm text-primary underline hover:no-underline"
          >
            Reload Profile
          </button>
        </div>
      </div>
    );
  }

  const displayName = `${profile?.firstName || ''} ${profile?.lastName || ''}`?.trim() || profile?.email;
  const memberSince = profile?.createdAt 
    ? new Date(profile.createdAt)?.toLocaleDateString('en-US', { year: 'numeric', month: 'long' })
    : 'Unknown';

  return (
    <div className="space-y-6">
      {/* Profile Header */}
      <div className="flex items-start space-x-4">
        <div className="relative">
          <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-border bg-muted flex items-center justify-center">
            {avatarPreview || profile?.avatarUrl ? (
              <Image
                src={avatarPreview || profile?.avatarUrl}
                alt="User avatar"
                className="w-full h-full object-cover"
              />
            ) : (
              <Icon name="User" size={32} className="text-muted-foreground" />
            )}
          </div>
          {uploadingAvatar && (
            <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
            </div>
          )}
        </div>
        
        <div className="flex-1 min-w-0">
          {!isEditing ? (
            <>
              <div className="flex items-center space-x-2 mb-1">
                <h3 className="text-lg font-semibold text-foreground">{displayName}</h3>
              </div>
              {profile?.username && (
                <p className="text-sm text-muted-foreground mb-2">@{profile?.username}</p>
              )}
              <p className="text-xs text-muted-foreground">Member since {memberSince}</p>
            </>
          ) : (
            <div className="space-y-3">
              {/* Profile Picture Upload Section */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-foreground mb-2">
                  Profile Picture
                </label>
                <div className="flex items-center space-x-3">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarUpload}
                    className="hidden"
                    id="avatar-upload-profile"
                    disabled={uploadingAvatar}
                  />
                  <label
                    htmlFor="avatar-upload-profile"
                    className={`inline-flex items-center px-4 py-2 border border-border rounded-lg text-sm font-medium text-foreground bg-card hover:bg-muted cursor-pointer transition-colors ${
                      uploadingAvatar ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {uploadingAvatar ? 'Uploading...' : avatarPreview ? 'Change Photo' : 'Upload Photo'}
                  </label>
                  {avatarPreview && (
                    <button
                      type="button"
                      onClick={handleRemoveAvatar}
                      className="text-sm text-red-600 hover:text-red-800 transition-colors"
                      disabled={uploadingAvatar}
                    >
                      Remove
                    </button>
                  )}
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  JPG, PNG or GIF. Max size 5MB.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Input
                  label="First Name"
                  value={formData?.firstName}
                  onChange={(e) => handleInputChange('firstName', e?.target?.value)}
                />
                <Input
                  label="Last Name"
                  value={formData?.lastName}
                  onChange={(e) => handleInputChange('lastName', e?.target?.value)}
                />
              </div>
              <Input
                label="Username"
                value={formData?.username}
                onChange={(e) => handleInputChange('username', e?.target?.value)}
                placeholder="@username"
                error={usernameError} // Pass the message string directly to 'error' prop
              />
            </div>
          )}
        </div>

        {/* Edit/Save/Cancel Buttons */}
        <div className="flex-shrink-0">
          {!isEditing ? (
            <Button
              variant="outline"
              size="sm"
              iconName="Edit"
              onClick={handleEdit}
            >
              Edit Profile
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                iconName="X"
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                iconName="Check"
                onClick={handleSave}
              >
                Save
              </Button>
            </div>
          )}
        </div>
      </div>
      {/* Bio */}
      <div className="space-y-2">
        <h4 className="text-sm font-medium text-foreground">About</h4>
        {!isEditing ? (
          <p className="text-sm text-muted-foreground leading-relaxed">
            {profile?.bio || 'No bio added yet'}
          </p>
        ) : (
          <textarea
            className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-input text-foreground resize-none"
            rows="4"
            value={formData?.bio}
            onChange={(e) => handleInputChange('bio', e?.target?.value)}
            placeholder="Tell us about yourself..."
          />
        )}
      </div>
      {/* Contact Info */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-foreground">Contact Information</h4>
        <div className="space-y-2">
          <div className="flex items-center space-x-3">
            <Icon name="Mail" size={16} className="text-muted-foreground" />
            <span className="text-sm text-muted-foreground">{profile?.email}</span>
          </div>
          {!isEditing ? (
            profile?.location && (
              <div className="flex items-center space-x-3">
                <Icon name="MapPin" size={16} className="text-muted-foreground" />
                <span className="text-sm text-muted-foreground">{profile?.location}</span>
              </div>
            )
          ) : (
            <Input
              label="Location"
              value={formData?.location}
              onChange={(e) => handleInputChange('location', e?.target?.value)}
              placeholder="City, State/Country"
            />
          )}
        </div>
      </div>
      {/* Achievements Section */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Award className="w-5 h-5 text-blue-600" />
            My Achievements
          </h3>
          {achievementStats && (
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-1 text-blue-600">
                <TrendingUp className="w-4 h-4" />
                <span className="font-semibold">{achievementStats?.totalPoints}</span>
                <span className="text-gray-600">points</span>
              </div>
              <div className="text-gray-600">
                {achievementStats?.totalAchievements} achievements
              </div>
            </div>
          )}
        </div>

        {achievements?.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Award className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No achievements yet. Start exploring to earn your first achievement!</p>
          </div>
        ) : (
          <>
            {/* Achievement Stats by Category */}
            {achievementStats && Object.keys(achievementStats?.byCategory)?.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                {Object.entries(achievementStats?.byCategory)?.map(([category, count]) => (
                  <div key={category} className="bg-gray-50 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-blue-600">{count}</div>
                    <div className="text-sm text-gray-600">{category}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Achievement List */}
            <div className="space-y-3">
              {achievements?.map(achievement => (
                <div
                  key={achievement?.id}
                  className="border rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start gap-3">
                    <span className="text-3xl">{achievement?.achievementIcon}</span>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-gray-900">
                          {achievement?.achievementName}
                        </h4>
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                          {achievement?.achievementPoints} pts
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">
                        {achievement?.achievementDescription}
                      </p>
                      <div className="flex items-center gap-3 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          {achievement?.categoryIcon} {achievement?.categoryName}
                        </span>
                        <span>•</span>
                        <span>
                          Earned {new Date(achievement.earnedAt)?.toLocaleDateString()}
                        </span>
                      </div>
                      {achievement?.notes && (
                        <p className="text-xs text-gray-500 mt-2 italic">
                          Note: {achievement?.notes}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
      {/* Addresses & Payment Section */}
      <AddressAndPaymentManager />
    </div>
  );
}